// ✅ src/pages/VehicleV2/LubeConsumptionReport_Print.jsx
// Reads query + navigate state + last saved search filters

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/lubeConsumptionReport";
const PRINT_FILTER_KEY = "LUBE_CONSUMPTION_PRINT_FILTERS";

const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => !toStr(v) || toStr(v).toLowerCase() === "select";

function prettify(k) {
  return String(k)
    .replace(/^fld_/i, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function buildColumns(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  return Object.keys(rows[0] || {})
    .slice(0, 20)
    .map((k) => ({ key: k, label: prettify(k) }));
}

function readSaved() {
  try {
    return JSON.parse(sessionStorage.getItem(PRINT_FILTER_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

export default function LubricationConsumptionReport_Print() {
  const navigate = useNavigate();
  const { state, search } = useLocation();
  const qs = useMemo(() => new URLSearchParams(search), [search]);

  const filters = useMemo(() => {
    const saved = readSaved();
    const f = state?.filters || state || {};
    const pick = (...vals) => {
      for (const v of vals) {
        const s = toStr(v);
        if (s && s.toLowerCase() !== "select") return s;
      }
      return "Select";
    };

    const suppliersFromQs = toStr(qs.get("suppliersCsv") || qs.get("supplier"))
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);

    return {
      branchId: pick(f.branchId, f.branch, qs.get("branchId"), qs.get("branch"), saved.branchId, saved.branch),
      month: pick(f.month, qs.get("month"), saved.month),
      year: pick(f.year, qs.get("year"), saved.year),
      itemCategoryId: pick(
        f.itemCategoryId,
        f.itemcategoryid,
        qs.get("itemCategoryId"),
        qs.get("itemcategoryid"),
        saved.itemCategoryId,
        "37"
      ),
      itemId: pick(f.itemId, f.itemname, qs.get("itemId"), qs.get("itemname"), saved.itemId),
      suppliers: Array.isArray(f.suppliers)
        ? f.suppliers
        : Array.isArray(saved.suppliers)
        ? saved.suppliers
        : suppliersFromQs,
      date: toStr(f.date || qs.get("date") || ""),
    };
  }, [state, qs]);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [error, setError] = useState("");

  const fetchPrint = async () => {
    try {
      setError("");
      setLoading(true);

      if (isSelect(filters.branchId) || isSelect(filters.month) || isSelect(filters.year)) {
        throw new Error("Select Branch, Month and Year on the report page, then click Print.");
      }

      const params = {
        branchId: filters.branchId,
        month: filters.month,
        year: filters.year,
        itemCategoryId: filters.itemCategoryId || "37",
        itemId: filters.itemId || "Select",
        date: filters.date || "",
      };
      if (filters.suppliers?.length) params.suppliers = filters.suppliers;

      const res = await axios.get(`${API_BASE}/list`, { params });
      if (!res.data?.success) throw new Error(res.data?.message || "Failed to load");

      setRows(Array.isArray(res.data.rows) ? res.data.rows : []);
      setColumns(
        Array.isArray(res.data.columns)
          ? res.data.columns
          : buildColumns(res.data.rows).map((c) => c.key)
      );
    } catch (e) {
      setError(e.message || "Failed to load print data");
      setRows([]);
      setColumns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrint();
  }, [filters.branchId, filters.month, filters.year, filters.itemId]);

  const monthName = useMemo(() => {
    const m = Number(filters.month);
    const arr = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    if (m >= 1 && m <= 12) return arr[m - 1];
    return filters.month;
  }, [filters.month]);

  const colDefs = useMemo(() => {
    if (columns?.length) return columns.map((k) => ({ key: k, label: prettify(k) }));
    return buildColumns(rows);
  }, [columns, rows]);

  return (
    <div className="min-h-screen bg-white p-4">
      <div className="print:hidden mb-6 text-center flex gap-3 justify-center">
        <button
          onClick={() => navigate(-1)}
          className="bg-gray-200 text-black px-6 py-3 text-sm font-medium hover:bg-gray-300"
          type="button"
        >
          Back
        </button>
        <button
          onClick={() => window.print()}
          disabled={loading || !!error}
          className="bg-black text-white px-6 py-3 text-sm font-medium hover:bg-gray-800 disabled:opacity-60"
          type="button"
        >
          {loading ? "Loading..." : "Print Report"}
        </button>
      </div>

      <div className="max-w-6xl mx-auto border-2 border-black p-4">
        <div className="border border-black p-3">
          <div className="border border-black p-3 mb-4 flex items-center gap-5">
            <div className="w-28 h-20 bg-black text-white flex flex-col justify-center items-center text-xs font-bold">
              <div>NISSI</div>
              <div>INFOTECH</div>
              <div className="text-xs">PVT LTD</div>
            </div>
            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold mb-2">Nissi Infotech</h1>
              <div className="text-xs leading-tight">
                <div className="font-semibold">CIVIL CONSTRUCTION</div>
                <div>3/42-12, Nagappa colony, kavundampalayam, Tamil Nadu, India.</div>
                <div className="mt-1">Phone No :9003744644</div>
              </div>
            </div>
          </div>

          <div className="text-center text-base font-bold my-4">Lubrication Consumption Report</div>

          <div className="border border-black p-2 text-xs mb-4">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="font-semibold">Branch</span> : {filters.branchId}
              </div>
              <div>
                <span className="font-semibold">Month</span> : {monthName}
              </div>
              <div>
                <span className="font-semibold">Year</span> : {filters.year}
              </div>
              <div>
                <span className="font-semibold">Item Category</span> : {filters.itemCategoryId}
              </div>
              <div className="col-span-2">
                <span className="font-semibold">Item</span> : {filters.itemId}
              </div>
              <div className="col-span-2">
                <span className="font-semibold">Supplier(s)</span> :{" "}
                {filters.suppliers?.length ? filters.suppliers.join(", ") : "-"}
              </div>
            </div>
          </div>

          {error ? (
            <div className="text-center text-sm p-6 border border-black">{error}</div>
          ) : loading ? (
            <div className="text-center text-sm p-6 border border-black">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="text-center text-sm p-6 border border-black">No data found.</div>
          ) : (
            <div className="border border-black overflow-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b border-black">
                    {colDefs.map((c) => (
                      <th key={c.key} className="border-r border-black p-2 text-left font-semibold">
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={idx} className="border-b border-black">
                      {colDefs.map((c) => (
                        <td key={c.key} className="border-r border-black p-2">
                          {toStr(r[c.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-6 p-2 border border-black bg-gray-100 text-xs">
            Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
          </div>
          <div className="text-right text-xs mt-2">INFRA SCREEN : B-60/1</div>
        </div>
      </div>
    </div>
  );
}